import React from 'react';
import styled from 'styled-components';
import ActionButton from '@/common/components/buttons/ActionButton';
import Input from '@/common/components/Input';
import TextArea from '@/common/components/TextArea';
import Loading from '@/common/components/Loading';
import { Colors } from '@/common/colors/colors';
import { Wrapper } from '../callAnalysis/callAnalysis.styles';
import useCallAnalysisConfig from './hooks/useCallAnalysisConfig';

const Section = styled.div`
	width: 100%;
	max-width: 1000px;
	border: 1px solid ${Colors.darkBlue};
	border-radius: 12px;
	padding: 20px;
	margin-bottom: 24px;
`;

const Title = styled.h3`
	color: ${Colors.darkBlue};
	margin-top: 0;
`;

const SubTitle = styled.h4`
	color: ${Colors.darkBlue};
	margin: 16px 0 8px;
`;

const Row = styled.div`
	display: flex;
	gap: 12px;
	align-items: center;
	margin-bottom: 10px;
	flex-wrap: wrap;
`;

const SectionId = styled.div`
	min-width: 180px;
	color: ${Colors.gray};
	font-size: 13px;
`;

const SectionBlock = styled.div`
	border: 1px solid ${Colors.gray};
	border-radius: 8px;
	padding: 16px;
	margin-bottom: 16px;
`;

const CriteriaList = styled.div`
	margin-top: 8px;
	padding-left: 12px;
	border-left: 3px solid ${Colors.darkBlue};
`;

const CriterionBlock = styled.div`
	padding: 10px 0;
	border-bottom: 1px solid ${Colors.gray};

	&:last-of-type {
		border-bottom: none;
	}
`;

const PromptPreview = styled.pre`
	width: 100%;
	max-height: 520px;
	overflow: auto;
	margin: 0;
	padding: 16px;
	box-sizing: border-box;
	background: #f7f9fc;
	border: 1px solid ${Colors.gray};
	border-radius: 8px;
	color: ${Colors.darkBlue};
	font-size: 13px;
	line-height: 1.45;
	white-space: pre-wrap;
	word-break: break-word;
`;

const CallAnalysisConfig = () => {
	const {
		config,
		promptPreview,
		isLoading,
		handleSave,
		loadPromptPreview,
		updateSection,
		addSection,
		removeSection,
		updateCriterion,
		addCriterion,
		removeCriterion,
		updateCriticalError,
		addCriticalError,
		removeCriticalError,
		updateConfigField,
	} = useCallAnalysisConfig();

	return (
		<>
			{isLoading && <Loading />}
			<Wrapper>
				{config && (
					<>
						<Section>
							<Title>AI analysis configuration</Title>
							<Row>
								<Input
									name="Name"
									id="name"
									value={config.name || ''}
									onChange={(e) => updateConfigField('name', e.target.value)}
									width={80}
									inputWidth={260}
									color={Colors.darkBlue}
								/>
							</Row>
							<label style={{ color: Colors.darkBlue }}>
								<input
									type="checkbox"
									checked={!!config.zero_score_on_critical_error}
									onChange={(e) =>
										updateConfigField(
											'zero_score_on_critical_error',
											e.target.checked,
										)
									}
								/>{' '}
								Zero score on critical error
							</label>
						</Section>

						<Section>
							<Title>Sections & criteria</Title>
							{(config.sections || []).map((section) => (
								<SectionBlock key={section.section_id}>
									<SectionId>ID: {section.section_id}</SectionId>
									<Row>
										<Input
											name="Label"
											id={`${section.section_id}_label`}
											value={section.label || ''}
											onChange={(e) =>
												updateSection(
													section.section_id,
													'label',
													e.target.value,
												)
											}
											width={80}
											inputWidth={280}
											color={Colors.darkBlue}
										/>
										<Input
											name="Weight (%)"
											id={`${section.section_id}_weight`}
											type="number"
											value={section.weight}
											onChange={(e) =>
												updateSection(
													section.section_id,
													'weight',
													e.target.value,
												)
											}
											width={100}
											inputWidth={100}
											color={Colors.darkBlue}
										/>
										<ActionButton
											defaultText="Delete section"
											color={Colors.red}
											onClick={() => removeSection(section.section_id)}
										/>
									</Row>
									<TextArea
										key={`${config._id}_${section.section_id}_description`}
										placeholder="Section description (used in AI prompt)"
										width="100%"
										defaultValue={section.description || ''}
										onChange={(e) =>
											updateSection(
												section.section_id,
												'description',
												e.target.value,
											)
										}
									/>

									<SubTitle>Criteria</SubTitle>
									<CriteriaList>
										{(section.criteria || []).map((criterion) => (
											<CriterionBlock key={criterion.criterion_id}>
												<Row>
													<label style={{ color: Colors.darkBlue }}>
														<input
															type="checkbox"
															checked={criterion.is_active !== false}
															onChange={(e) =>
																updateCriterion(
																	section.section_id,
																	criterion.criterion_id,
																	'is_active',
																	e.target.checked,
																)
															}
														/>{' '}
														Active
													</label>
													<Input
														name="Label"
														id={`${criterion.criterion_id}_label`}
														value={criterion.label || ''}
														onChange={(e) =>
															updateCriterion(
																section.section_id,
																criterion.criterion_id,
																'label',
																e.target.value,
															)
														}
														width={80}
														inputWidth={280}
														color={Colors.darkBlue}
													/>
													<ActionButton
														defaultText="Delete"
														color={Colors.red}
														onClick={() =>
															removeCriterion(
																section.section_id,
																criterion.criterion_id,
															)
														}
													/>
												</Row>
												<TextArea
													key={`${config._id}_${criterion.criterion_id}_description`}
													placeholder="What the AI should check"
													width="100%"
													defaultValue={criterion.description || ''}
													onChange={(e) =>
														updateCriterion(
															section.section_id,
															criterion.criterion_id,
															'description',
															e.target.value,
														)
													}
												/>
											</CriterionBlock>
										))}
										{!(section.criteria || []).length && (
											<div style={{ color: Colors.gray, marginBottom: 8 }}>
												No criteria in this section yet.
											</div>
										)}
										<ActionButton
											defaultText="Add criterion"
											onClick={() => addCriterion(section.section_id)}
										/>
									</CriteriaList>
								</SectionBlock>
							))}
							<ActionButton defaultText="Add section" onClick={addSection} />
						</Section>

						<Section>
							<Title>Critical errors</Title>
							{(config.critical_errors || []).map((error) => (
								<div
									key={error.error_id}
									style={{
										borderBottom: `1px solid ${Colors.gray}`,
										paddingBottom: 12,
										marginBottom: 12,
									}}
								>
									<Row>
										<label style={{ color: Colors.darkBlue }}>
											<input
												type="checkbox"
												checked={error.is_active !== false}
												onChange={(e) =>
													updateCriticalError(
														error.error_id,
														'is_active',
														e.target.checked,
													)
												}
											/>{' '}
											Active
										</label>
										<Input
											name="Label"
											id={`${error.error_id}_label`}
											value={error.label || ''}
											onChange={(e) =>
												updateCriticalError(
													error.error_id,
													'label',
													e.target.value,
												)
											}
											width={80}
											inputWidth={320}
											color={Colors.darkBlue}
										/>
										<ActionButton
											defaultText="Delete"
											color={Colors.red}
											onClick={() => removeCriticalError(error.error_id)}
										/>
									</Row>
									<TextArea
										placeholder="Description"
										width="100%"
										defaultValue={error.description || ''}
										onChange={(e) =>
											updateCriticalError(
												error.error_id,
												'description',
												e.target.value,
											)
										}
									/>
								</div>
							))}
							<ActionButton
								defaultText="Add critical error"
								onClick={addCriticalError}
							/>
						</Section>

						<Section>
							<Title>Prompt preview</Title>
							<Row>
								<ActionButton
									defaultText="Refresh preview"
									onClick={() => loadPromptPreview(config)}
								/>
							</Row>
							<PromptPreview>
								{promptPreview ||
									'Click "Refresh preview" to generate the prompt.'}
							</PromptPreview>
						</Section>

						<div style={{ width: '100%', maxWidth: 1000, textAlign: 'right' }}>
							<ActionButton
								defaultText="Save configuration"
								onClick={handleSave}
							/>
						</div>
					</>
				)}
			</Wrapper>
		</>
	);
};

export default CallAnalysisConfig;
